/**
 * #4488: how long does the app wait, after a web purchase, for the payment provider's
 * webhook to land before it gives up and shows the buyer an alert?
 *
 * There are three places that wait for the same kind of thing, and until this file
 * existed each one carried its own copied pair of numbers:
 *
 *   - `finalizePaddleSubscriptionAsync` in `subscribe.ts` — the buyer was ALREADY signed
 *     in, so the webhook only has to grant the licence on an account that exists.
 *   - the coach-seat wait in `coach-checkout-page.ts` — same shape, same thing waited for.
 *   - `finalizePurchaseFirstSignInAsync` in `subscribe.ts` — the buyer paid while LOGGED
 *     OUT, so the webhook has to CREATE the account first, and only then can the server
 *     mint the claim token this loop is polling for.
 *
 * THE RULE THIS FILE EXISTS TO HOLD. The purchase-first wait must never be the shortest
 * of the three. It waits for strictly more to happen than the other two, and what it ends
 * at is worse for the buyer than a late success: an alert saying the payment registration
 * was delayed, then a reload back to the logged-out subscribe page. That is a bad place to
 * send someone who has just paid us, so we would rather wait longer and let it succeed.
 *
 * It was the shortest anyway — 20 attempts at 1.5 s, about 30 seconds, against about 90
 * seconds for the other two — and that is what #4488 fixes. The numbers were literals in
 * three files plus a fourth copy written out in prose in an E2E helper comment, and that
 * prose copy had already drifted into saying something untrue. One named home for them is
 * what stops the next copy drifting too.
 *
 * WHY THE PURCHASE-FIRST CADENCE STAYS AT 1.5 SECONDS. The obvious way to reach 90 seconds
 * would be to copy the other two exactly (30 attempts at 3 s). We deliberately do not. The
 * cadence decides how quickly a buyer is signed in once their account IS ready, and this is
 * the one flow where the buyer is sitting in front of a spinner with no session at all.
 * Keeping 1.5 s means the successful case is exactly as fast as it was before #4488, and
 * only the failing tail gets longer. The price is up to 60 claim-token calls instead of 20
 * in the worst case, which is a cheap read on a path that runs once per purchase.
 */

import { sleep } from '@cf-shared/shared-util';

/** One flow's wait: how often to poll, and how many times before giving up. */
export interface PostPurchaseWaitBudget {
  /** How long to wait between two polls, in milliseconds. */
  readonly pollIntervalMs: number;
  /** How many polls before the flow gives up and shows the buyer the alert. */
  readonly maxAttempts: number;
}

/** The buyer was already signed in; the webhook only has to grant the licence. */
export const kAlreadySignedInWaitBudget: PostPurchaseWaitBudget = {
  pollIntervalMs: 3000,
  maxAttempts: 30,
};

/** A coach buying seats. Signed in already, so this waits for the same thing as above. */
export const kCoachSeatWaitBudget: PostPurchaseWaitBudget = {
  pollIntervalMs: 3000,
  maxAttempts: 30,
};

/**
 * The buyer paid while logged out. The webhook has to create the account before the
 * server can mint the claim token this waits for, so it waits for strictly more than the
 * two budgets above and may never be given less time than they are.
 */
export const kPurchaseFirstWaitBudget: PostPurchaseWaitBudget = {
  pollIntervalMs: 1500,
  maxAttempts: 60,
};

/**
 * The longest this budget can keep a buyer waiting, in milliseconds.
 *
 * The wait sleeps BETWEEN attempts and skips the sleep after the last one, so a budget of
 * `n` attempts sleeps `n - 1` times. This counts the sleeps only; the server round trips
 * themselves are on top and vary.
 */
export function totalWaitMs(budget: PostPurchaseWaitBudget): number {
  return (budget.maxAttempts - 1) * budget.pollIntervalMs;
}

/** What one post-purchase wait ended up doing. */
export interface PostPurchasePollResult<T> {
  /**
   * What the attempt answered with, or undefined when the budget ran out without an
   * answer, or when the wait was cancelled.
   */
  readonly result?: T;
  /**
   * True when `isCancelled()` answered true — the buyer left the page mid-wait. The
   * caller must return without alerting, reporting or reloading: there is nobody there.
   */
  readonly cancelled: boolean;
  /**
   * How many attempts actually ran. The give-up path prices the elapsed wait from this,
   * by passing it to `totalWaitMs` as the attempt count that really happened.
   */
  readonly attemptsMade: number;
}

/** What a post-purchase wait needs from the flow that is waiting. */
export interface PostPurchasePollHandlers<T> {
  /** True once there is nobody left to wait for — the page's view was torn down. */
  isCancelled(): boolean;
  /**
   * One poll. Answer `undefined` to keep waiting, or any defined value to stop the wait
   * and hand that value back to the caller. A flow with more than one way to stop answers
   * with a value that says which happened.
   */
  attemptAsync(): Promise<T | undefined>;
}

/**
 * Run one flow's post-purchase wait: poll until it answers, the budget runs out, or the
 * buyer leaves the page.
 *
 * THIS IS WHY THE FUNCTION IS HERE rather than written out per flow. `totalWaitMs` above
 * prices a budget as `(maxAttempts - 1) * pollIntervalMs`, and that arithmetic is only
 * right because the waiting sleeps BETWEEN attempts and never after the last one. Three
 * flows used to write that loop out by hand, each re-deriving the same `attempt <
 * maxAttempts - 1` condition, with nothing tying it to the arithmetic that depends on it.
 * A fourth flow written the obvious way — one sleep per attempt — would have made
 * `totalWaitMs` quietly wrong for it, including the `waitedMs` its timeout reports, while
 * still compiling and still passing this module's own tests. The rule and the sum that
 * relies on it now live in one file, and no caller can get it wrong by not knowing it.
 */
export async function pollForPostPurchaseAsync<T>(
  budget: PostPurchaseWaitBudget,
  handlers: PostPurchasePollHandlers<T>,
): Promise<PostPurchasePollResult<T>> {
  const { pollIntervalMs, maxAttempts } = budget;
  let attemptsMade = 0;
  // for each attempt in the budget, stopping early if the buyer has left
  for (let attempt = 0; attempt < maxAttempts && !handlers.isCancelled(); attempt++) {
    attemptsMade++;
    const result = await handlers.attemptAsync();
    // an answer ends the wait immediately — the caller decides what it means
    if (result !== undefined) {
      return { result, cancelled: false, attemptsMade };
    }
    // the attempt may have taken long enough for the buyer to leave
    if (handlers.isCancelled()) {
      return { cancelled: true, attemptsMade };
    }
    // wait before the next attempt, and never after the last one — see totalWaitMs
    if (attempt < maxAttempts - 1) {
      await sleep(pollIntervalMs);
    }
  }
  // either the budget ran out with no answer, or the buyer left before the first attempt
  return { cancelled: handlers.isCancelled(), attemptsMade };
}
